import type { UseQueryOptions } from "@tanstack/react-query";

import { useApiMutation } from "@/hooks/api/use-api-mutation";
import { useApiQuery } from "@/hooks/api/use-api-query";
import type { ApiError } from "@/lib/api-client";

export type OrganizationSummary = {
  id: string;
  name: string;
  slug: string | null;
  plan: string;
  createdAt: string;
  updatedAt: string;
  enableCdr: boolean | null;
  enableRecording: boolean | null;
  enablePrefixMatching: boolean | null;
  mediaBypass: boolean | null;
  webhookEnabled: boolean | null;
  webhookUrl: string | null;
  strategyType: string;
  membershipRole: string | null;
  cdrRetentionDays: number | null;
  recordingRetentionDays: number | null;
};

export type OrganizationsResponse = {
  organizations: OrganizationSummary[];
  activeOrganizationId: string | null;
  isAdmin: boolean;
};

export function useOrganizationsQuery(
  options?: Omit<
    UseQueryOptions<OrganizationsResponse, ApiError>,
    "queryKey" | "queryFn"
  >
) {
  return useApiQuery<OrganizationsResponse, ApiError>({
    queryKey: ["organizations"],
    path: "/api/organizations",
    queryOptions: options,
  });
}

type CreateOrganizationVariables = {
  id?: string;
  name: string;
  slug?: string | null;
  logo?: string | null;
  plan?: string | null;
  enableCdr?: boolean | null;
  enableRecording?: boolean | null;
  enablePrefixMatching?: boolean | null;
  mediaBypass?: boolean | null;
  cdrRetentionDays?: number | null;
  recordingRetentionDays?: number | null;
  webhookEnabled?: boolean | null;
  webhookUrl?: string | null;
  strategyType?: string | null;
};

export function useCreateOrganizationMutation() {
  return useApiMutation<
    { organization: OrganizationSummary },
    CreateOrganizationVariables
  >({
    getRequest: (body) => ({
      path: "/api/organizations",
      method: "POST",
      body,
    }),
  });
}

type UpdateOrganizationVariables = {
  id: string;
  data: Partial<CreateOrganizationVariables>;
};

export function useUpdateOrganizationMutation() {
  return useApiMutation<
    { organization: OrganizationSummary },
    UpdateOrganizationVariables
  >({
    getRequest: ({ id, data }) => ({
      path: `/api/organizations/${id}`,
      method: "PATCH",
      body: data,
    }),
  });
}

export function useDeleteOrganizationMutation() {
  return useApiMutation<{ success: boolean }, { id: string }>({
    getRequest: ({ id }) => ({
      path: `/api/organizations/${id}`,
      method: "DELETE",
    }),
  });
}
